import React, { FC, useRef, useState, useEffect } from 'react';
import moment from 'moment';
import { contentModalViewStore } from 'src/store/modal';
import {
  preworkDataStore,
  initPreworkPayload,
  requiredPreworkSet,
  checkRequired,
} from 'src/store/preworkInfo';
import { useQueryClient } from '@tanstack/react-query';
import { getMemberInfo } from 'src/utils/common';
import { dateCalendarFormatType, currentTimeFormat } from 'src/utils/dateSet';
import { errorModalViewStore, loadingModalViewStore, confirmModalViewStore } from 'src/store/modal';
import {
  fetchInsertPreworkData,
  fetchUpdatePreworkData,
  fetchDeletePreworkData,
} from 'src/fetch/stepB';
import DefaultButton from 'src/components/atoms/buttons/DefaultButton';
import HelpButton from 'src/components/atoms/buttons/HelpButton';
import GridList from './List';
import InputDiv from './InputDiv';
import AddGrid from './add';
import { validateBizNo } from 'src/utils/charSet';
import '../tableRef.scss';
import './style.scss';

interface Props {
  type: string;
  data: any;
}
const CompnyGrid: FC<Props> = ({ type, data }) => {
  const userInfoData: any = getMemberInfo();
  const [viewType, setViewType] = useState({ view: type });

  const queryClient = useQueryClient();
  const errorModalState = errorModalViewStore((state: any) => state.set);
  const loadingState = loadingModalViewStore((state: any) => state.set);
  const setConfirmModalState = confirmModalViewStore((state: any) => state.set);
  const closeModalState = confirmModalViewStore((state: any) => state.close);

  const setContentModalState = contentModalViewStore((state: any) => state.set);
  const preworkPayload = preworkDataStore((state: any) => state.payload);
  const bindPreworkPayload = preworkDataStore((state: any) => state.bindPayload);
  const bindPreworkParam = preworkDataStore((state: any) => state.param);
  const bindPreworkBindParam = preworkDataStore((state: any) => state.bindParam);
  const viewData: any = data;

  // 근무기간 기본값: 귀속연도(acct_year) 1월 1일 ~ 12월 31일
  const acctYear = parseInt(userInfoData?.acct_year, 10) || new Date().getFullYear();
  const defaultStartDate = `${acctYear}0101`;
  const defaultEndDate = `${acctYear}1231`;

  useEffect(() => {
    if (Object.keys(data).length === 0) {
      bindPreworkPayload({
        ...initPreworkPayload,
        start_date: defaultStartDate,
        end_date: defaultEndDate,
        red_start_date: '',
        red_end_date: '',
      });
    } else {
      bindPreworkPayload({ ...data });
    }
  }, [data]);

  const ocrEvt = () => {};
  const closeEvt = () => {
    setContentModalState({
      active: '',
    });
  };
  const insertDataEvt = () => {
    preworkPayload.acct_year = userInfoData.acct_year;
    preworkPayload.co_cd = userInfoData.co_cd;
    preworkPayload.co_id = userInfoData.co_id;
    preworkPayload.name = userInfoData.name;

    const param = preworkPayload;
    // validation
    const { status } = validationParam(param, 'insert');
    if (!status) {
      return false;
    }

    //사업번호 - 제거
    if (preworkPayload.prev_co_num.indexOf('-') !== -1) {
      preworkPayload.prev_co_num = preworkPayload.prev_co_num.replaceAll('-', '');
    }

    if (param.start_date === '') param.start_date = currentTimeFormat('YYYYMMDD');
    if (param.end_date === '') param.end_date = currentTimeFormat('YYYYMMDD');

    fetchInsertPreworkData(param, queryClient, errorModalState, loadingState, () => {
      const initialSeq = bindPreworkParam.initialSeq || 0;
      bindPreworkBindParam({ initialSeq: initialSeq + 1 });
      setContentModalState({
        active: '',
      });
    });
  };
  const help = {
    title: '파일에서 종전 근무지 정보를 자동 추출합니다.',
    content: '(근로소득 원천징수 영수증 파일 선택)',
  };

  // DELETE
  const deleteEvt = () => {
    setConfirmModalState({
      active: '_active',
      content: (
        <div className="_title">
          <div className="_main">{data.prev_co_name}</div>
          <div className="_sub">해당 내용을 삭제하시겠습니까?</div>
        </div>
      ),
      cancleEvt: () => {
        closeModalState({ content: null, active: '' });
      },
      confirmEvt: () => {
        const param: any = {};
        param.acct_year = userInfoData.acct_year;
        param.co_cd = userInfoData.co_cd;
        param.co_id = userInfoData.co_id;
        param.prev_co_num = data.prev_co_num;
        param.start_date = data.start_date;

        fetchDeletePreworkData(param, queryClient, errorModalState, loadingState, () => {
          closeModalState({ content: null, active: '' });
          const initialSeq = bindPreworkParam.initialSeq || 0;
          bindPreworkBindParam({ initialSeq: initialSeq + 1 });
          setContentModalState({
            active: '',
          });
        });
      },
    });
  };

  // UPDATE
  const updateEvt = () => {
    setViewType({ ...viewType, view: 'edit' });
  };

  // UPDATE
  const updateDataEvt = () => {
    preworkPayload.acct_year = userInfoData.acct_year;
    preworkPayload.co_cd = userInfoData.co_cd;
    preworkPayload.co_id = userInfoData.co_id;
    preworkPayload.name = userInfoData.name;
    const param = preworkPayload;

    // validation
    const { status } = validationParam(param, 'update');
    if (!status) {
      return false;
    }

    // return false;
    fetchUpdatePreworkData(param, queryClient, errorModalState, loadingState, () => {
      const initialSeq = bindPreworkParam.initialSeq || 0;
      bindPreworkBindParam({ initialSeq: initialSeq + 1 });
      setContentModalState({
        active: '',
      });
    });
  };

  // CANCLE
  const cancleEvt = () => {
    setViewType({ ...viewType, view: 'list' });
    bindPreworkPayload({ ...data });
  };

  const validationParam = (payloadSet: any, type: string) => {
    const resSet = { errText: { main: '', sub: '' }, status: true };

    // Check Required
    for (const item in payloadSet) {
      if (checkRequired(item) === 'Y') {
        let chkValue = payloadSet[item];
        if (typeof chkValue === 'string') chkValue = chkValue.trim() || '';
        if (typeof chkValue === 'number') chkValue = chkValue || 0;
        if (chkValue === '' || chkValue === 0) {
          const label = requiredPreworkSet.find((el: any) => el.id === item)?.label;
          resSet.errText.main = `'${label}' 은 필수입력 입니다.`;
          resSet.errText.sub = '해당 값을 입력해 주세요.';
          resSet.status = false;
        }
      }
    }

    if (type === 'insert') {
      //사업자등록번호 체크
      // 123-45-67891
      // 314-15-92650
      // 870-10-23454
      // 555-66-77780
      // 901-23-45674
      if (!validateBizNo(payloadSet.prev_co_num || '')) {
        const targetName = '사업자등록번호';
        resSet.errText.main = `잘못된 ${targetName}입니다.`;
        resSet.errText.sub = `${targetName}를 다시 입력해 주세요.`;
        resSet.status = false;
      }
    }

    // 날짜 형식(YYYYMMDD) 체크
    const isValidDateFormat = (val: string) =>
      /^\d{8}$/.test(val) && moment(val, 'YYYYMMDD', true).isValid();

    // 근무기간 시작/종료: 필수 + YYYYMMDD 형식만 허용
    const dateRequiredSet = [
      { id: 'start_date', label: '근무기간 시작' },
      { id: 'end_date', label: '근무기간 종료' },
    ];
    dateRequiredSet.forEach(({ id, label }) => {
      if (resSet.status && !isValidDateFormat(payloadSet[id] || '')) {
        resSet.errText.main = `'${label}' 값이 올바르지 않습니다.`;
        resSet.errText.sub = '날짜 값이 올바르지 않습니다.';
        resSet.status = false;
      }
    });

    // 감면기간 시작/종료: 공백은 허용, 값이 있으면 YYYYMMDD 형식만 허용
    const dateOptionalSet = [
      { id: 'red_start_date', label: '감면기간 시작' },
      { id: 'red_end_date', label: '감면기간 종료' },
    ];
    dateOptionalSet.forEach(({ id, label }) => {
      const val = payloadSet[id] || '';
      if (resSet.status && val !== '' && !isValidDateFormat(val)) {
        resSet.errText.main = `'${label}' 값이 올바르지 않습니다.`;
        resSet.errText.sub = '날짜 값이 올바르지 않습니다.';
        resSet.status = false;
      }
    });

    if (!resSet.status)
      errorModalState({
        data: { mainTitle: resSet.errText.main, subTitle: resSet.errText.sub },
        active: '_active',
      });

    return resSet;
  };

  // TOP
  const topDiv = () => {
    if (viewType.view === 'add') {
      return (
        <div className="_top_div">
          <div className="_btn">
            <DefaultButton
              text="OCR 입력"
              styleDef={{ width: '130px', borderRadius: '6px', fontSize: '13px' }}
              clickEvent={e => ocrEvt()}
              icon="ri-focus-mode"
              type="type3"
              iconStyleDef={{ fontSize: '18px' }}
            />
          </div>
          <div className="_help_div">
            <HelpButton content={help.content} title={help.title} type="type2" />
          </div>
        </div>
      );
    }
    if (viewType.view === 'edit') {
      return (
        <div className="_top_div">
          <div className="_setting_div">
            <div className="_btn_div">
              <div className="_btn">
                <DefaultButton
                  icon="ri-edit-box-line"
                  clickEvent={updateDataEvt}
                  type="type3"
                  text="근무지 변경"
                  styleDef={{
                    fontWeight: '500',
                    width: '120px',
                    height: '30px',
                    fontSize: '14px',
                  }}
                />
              </div>
              <div className="_btn">
                <DefaultButton
                  icon="ri-close-line"
                  clickEvent={cancleEvt}
                  type="type1"
                  text="취소"
                  styleDef={{
                    fontWeight: '500',
                    width: '80px',
                    height: '30px',
                    fontSize: '14px',
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      );
    }
    return (
      <div className="_top_div">
        <div className="_setting_div">
          <div className="_btn_div">
            <div className="_btn">
              <DefaultButton
                icon="ri-edit-box-line"
                clickEvent={updateEvt}
                type="type2"
                text="근무지 수정"
                styleDef={{
                  fontWeight: '500',
                  width: '120px',
                  height: '30px',
                  fontSize: '14px',
                }}
              />
            </div>
          </div>
        </div>
      </div>
    );
  };
  // BOTTOM
  const bottomDiv = () => {
    if (viewType.view === 'add') {
      return (
        <div className="_bottom_div">
          <div className="_btn">
            <DefaultButton
              text="근무지 추가"
              styleDef={{ width: '300px', height: '43px' }}
              clickEvent={e => insertDataEvt()}
              icon=""
              type="type5"
              iconStyleDef={{ fontSize: '18px' }}
            />
          </div>
        </div>
      );
    }
    if (viewType.view === 'edit') {
      return (
        <div className="_bottom_div">
          <div className="_btn">
            <DefaultButton
              text="근무지 변경"
              styleDef={{ width: '300px', height: '43px' }}
              clickEvent={e => updateDataEvt()}
              icon=""
              type="type5"
              iconStyleDef={{ fontSize: '18px' }}
            />
          </div>
        </div>
      );
    }
    return (
      <div className="_bottom_div">
        <div className="_btn">
          <DefaultButton
            text="확인"
            styleDef={{ width: '300px', height: '38px' }}
            clickEvent={e => closeEvt()}
            icon=""
            type="type5"
            iconStyleDef={{ fontSize: '18px' }}
          />
        </div>
      </div>
    );
  };
  const viewGrid = () => {
    return (
      <div className="_list_grid">
        {/* TOP_DIV */}
        {topDiv()}
        <InputDiv data={data} type={viewType.view} />
        <GridList data={data} type={viewType.view} />
        {/* BOTTOM_DIV */}
        {bottomDiv()}
      </div>
    );
  };

  return (
    <div className="_company_before_detail_div_layout">
      <div className="_content_div">{viewGrid()}</div>
    </div>
  );
};

export default CompnyGrid;
